import { Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AdminNavigation } from './AdminNavigation';
import { Text, Heading } from '../ui/Text';
import { Button } from '../ui/Button';
import { ArrowLeftIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useState } from 'react';

/**
 * AdminLayout provides the main layout structure for admin pages
 * Includes sidebar navigation and main content area
 * Designed to be extensible for future admin features
 */
export function AdminLayout() {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="hidden md:flex md:w-64 md:flex-col">
          <div className="flex flex-col flex-grow border-r border-border bg-card">
            {/* Admin header */}
            <div className="flex items-center h-16 px-4 border-b border-border">
              <div className="flex items-center">
                <Heading level={2} size="lg" className="font-bold text-primary">
                  Admin Panel
                </Heading>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex-1 px-3 space-y-1">
                <AdminNavigation />
              </div>

              {/* User info */}
              <div className="flex-shrink-0 px-4 mt-5 pt-5 border-t border-border">
                <Text size="xs" variant="muted" className="mb-1">
                  Signed in as
                </Text>
                <Text size="sm" className="font-medium truncate">
                  {user?.name || user?.email}
                </Text>
                <Text size="xs" variant="muted">
                  Administrator
                </Text>
              </div>

              {/* Back to app */}
              <div className="flex-shrink-0 px-3 mt-4">
                <Link to="/dashboard">
                  <Button variant="outline" size="sm" className="w-full">
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to App
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Mobile navigation header */}
          <div className="md:hidden">
            <div className="flex items-center justify-between h-16 px-4 border-b border-border bg-card">
              <Heading level={2} size="md" className="font-bold text-primary">
                Admin Panel
              </Heading>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? (
                    <XMarkIcon className="h-5 w-5" />
                  ) : (
                    <Bars3Icon className="h-5 w-5" />
                  )}
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline" size="sm">
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Back</span>
                  </Button>
                </Link>
              </div>
            </div>

            {/* Mobile navigation menu - collapsible */}
            {mobileMenuOpen && (
              <div className="border-b border-border bg-card shadow-lg">
                <div className="px-2 py-3" onClick={() => setMobileMenuOpen(false)}>
                  <AdminNavigation />
                </div>
              </div>
            )}
          </div>

          {/* Page content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
